import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Form, notification, Typography } from "antd";
import clsx from "clsx";
import { useForm, FormProvider } from "react-hook-form";
import { useObject } from "@entities/object";
import { useCoordinator } from "@shared/lib/hooks";
import { useIsUnifiedMode } from "@shared/utils";
import { formToObjectMapper } from "../../model/mappers/form-to-object.mapper";
import { objectToFormMapper } from "../../model/mappers/object-to-form.mapper";
import { ObjectFormEditSchema } from "../../model/object-form-edit.schema";
import { useUpdateObject } from "../../model/use-update-object";
import type { FormType } from "../../types/form-type";
import { ObjectFormClose } from "../object-form-close/object-form-close";
import { ObjectFormDeadline } from "../object-form-deadline/object-form-deadline";
import { ObjectFormExaminations } from "../object-form-examinations/object-form-examinations";
import { ObjectFormGeneral } from "../object-form-general/object-form-general";
import { ObjectFormPermissions } from "../object-form-permissions/object-form-permissions";
import "./object-form-create.scss";

export const ObjectFormEdit = () => {
  const { projectId, objectId } = useCoordinator();
  const isUnifiedMode = useIsUnifiedMode();

  const { data: object } = useObject({
    objectId,
  });

  const { mutate } = useUpdateObject({ projectId, objectId });

  const formDefaults = objectToFormMapper(object);

  const { control, handleSubmit, reset, ...formRest } = useForm<FormType>({
    // defaultValues: formDefaults,
    resolver: zodResolver(ObjectFormEditSchema),
    shouldUnregister: true,
  });

  useEffect(() => {
    reset(formDefaults);
  }, [formDefaults, reset]);

  const onSubmit = (data: FormType) => {
    const dto = formToObjectMapper(data, object);
    mutate(
      {
        type: "PART",
        buildingObjectId: objectId,
        ...dto,
      },
      {
        onSuccess: () =>
          notification.success({
            message: "Объект строительства успешно обновлён",
          }),
        onError: () =>
          notification.error({
            message: "Не удалось обновить объект строительства",
          }),
      },
    );
  };

  return (
    <div
      className={clsx("project-object-wrapper", isUnifiedMode && "project-object-wrapper--unified")}
    >
      <div className="project-object-container">
        <Typography.Title className="project-object-title" level={3}>
          Редактирование объекта
        </Typography.Title>
        <FormProvider {...{ ...formRest, control, reset, handleSubmit }}>
          <Form className="custom-form" layout="vertical">
            <ObjectFormGeneral />

            <div className="form-section">
              <Typography.Title
                style={{
                  marginBottom: "24px",
                }}
                level={5}
              >
                Адрес
              </Typography.Title>
              <Typography.Text>{object.address.addressString}</Typography.Text>
            </div>

            <ObjectFormPermissions />

            <ObjectFormExaminations />

            <ObjectFormDeadline />

            <div className="object-button-container">
              <Button
                type="primary"
                onClick={handleSubmit(onSubmit)}
              >
                Сохранить
              </Button>
            </div>
          </Form>
          <ObjectFormClose />
        </FormProvider>
      </div>
    </div>
  );
};
